/**
 * Canonical Desktop Tabulator runtime i18n (display-layer only).
 *
 * Contract:
 * - Update visible titles / filter option labels / placeholders IN PLACE
 * - Do NOT destroy/recreate the table
 * - Do NOT reload remote/progressive data for language-only changes
 * - Do NOT mutate raw filter values, mask field names, or API tokens
 * - Prefer setTabulatorColumnTitle (avoid Column.updateDefinition crash path)
 *
 * Progressive vs non-progressive: this helper is ownership-agnostic.
 */

import {
  applyTabulatorColumnTitles,
  setTabulatorColumnTitle,
} from './tabulatorColumnTitle';

export { applyTabulatorColumnTitles, setTabulatorColumnTitle };

function isDomElement(el) {
  return !!(el && typeof el.setAttribute === 'function' && typeof el.querySelector === 'function');
}

/**
 * Update header-filter display labels/placeholders without changing raw values.
 *
 * @param {object|null} table
 * @param {Record<string, {
 *   placeholder?: string,
 *   options?: Array<{ label: string, value: any }>,
 *   values?: Record<string, string>,
 * }>} [byField]
 */
export function applyTabulatorHeaderFilterDisplay(table, byField) {
  if (!table || typeof table.getColumns !== 'function' || !byField) return;
  const columns = table.getColumns();
  for (const col of columns) {
    const field = typeof col.getField === 'function' ? col.getField() : null;
    if (!field || !byField[field]) continue;
    const cfg = byField[field];
    const def = typeof col.getDefinition === 'function' ? col.getDefinition() : null;
    if (!def || typeof def !== 'object') continue;

    const prevParams = def.headerFilterParams && typeof def.headerFilterParams === 'object'
      ? def.headerFilterParams
      : {};
    const nextParams = { ...prevParams };

    if (cfg.placeholder != null) {
      nextParams.placeholder = cfg.placeholder;
    }
    if (Array.isArray(cfg.options)) {
      // Replace option labels; keep caller-supplied raw values.
      nextParams.options = cfg.options.map((opt) => ({
        label: opt.label,
        value: opt.value,
      }));
    }
    if (cfg.values && typeof cfg.values === 'object') {
      // values map: keys are raw tokens (immutable), values are display labels.
      nextParams.values = { ...cfg.values };
    }

    def.headerFilterParams = nextParams;

    try {
      if (typeof col.reloadHeaderFilter === 'function') {
        col.reloadHeaderFilter();
      }
    } catch (_) {
      /* keep live filters */
    }

    // Best-effort placeholder text on visible filter input.
    if (cfg.placeholder != null) {
      try {
        const el = typeof col.getElement === 'function' ? col.getElement() : null;
        if (isDomElement(el)) {
          const input = el.querySelector('.tabulator-header-filter input, .tabulator-header-filter textarea');
          if (input && input.getAttribute) {
            input.setAttribute('placeholder', String(cfg.placeholder));
          }
        }
      } catch (_) {
        /* ignore */
      }
    }
  }
}

/**
 * Collect Tabulator row components without treating an empty group as "done".
 * IMPORTANT: `getRows('visible')` often returns `[]` (truthy) during locale
 * effects / virtual-DOM timing — the previous `rows || getRows()` short-circuit
 * skipped reformatting and left reactFormatter cells on the old language.
 *
 * @param {object} table
 * @returns {object[]}
 */
function collectTabulatorRowsForLocaleReformat(table) {
  const tryGet = (group) => {
    try {
      const rows = group === undefined ? table.getRows() : table.getRows(group);
      return Array.isArray(rows) ? rows : [];
    } catch (_) {
      return [];
    }
  };

  let rows = tryGet('active');
  if (rows.length === 0) rows = tryGet('visible');
  if (rows.length === 0) rows = tryGet();
  return rows;
}

/**
 * Re-invoke react-tabulator reactFormatter for cells that still have `.formatterCell`.
 * Row.reformat() → reinitialize() no-ops when `element.offsetParent === null`, which
 * leaves ReactDOM trees painted in the previous locale. Calling the formatter again
 * schedules ReactDOM.render against live locale refs without ajax/replaceData.
 *
 * @param {object[]} rows
 * @returns {number}
 */
function forceReactFormatterLocalePaint(rows) {
  if (!Array.isArray(rows)) return 0;
  let painted = 0;
  for (const row of rows) {
    let cells;
    try {
      cells = typeof row.getCells === 'function' ? row.getCells() : [];
    } catch (_) {
      cells = [];
    }
    if (!Array.isArray(cells)) continue;
    for (const cell of cells) {
      try {
        const el = typeof cell.getElement === 'function' ? cell.getElement() : null;
        if (!el || typeof el.querySelector !== 'function') continue;
        if (!el.querySelector('.formatterCell')) continue;
        const col = typeof cell.getColumn === 'function' ? cell.getColumn() : null;
        const def = col && typeof col.getDefinition === 'function' ? col.getDefinition() : null;
        const formatter = def && typeof def.formatter === 'function' ? def.formatter : null;
        if (!formatter) continue;
        let renderedCb = null;
        // Invoke formatter for its onRendered/setTimeout React paint; ignore returned HTML
        // so we do not wipe the existing cell shell.
        formatter(cell, def.formatterParams || {}, (cb) => {
          if (typeof cb === 'function') renderedCb = cb;
        });
        if (typeof renderedCb === 'function') {
          renderedCb();
          painted += 1;
        }
      } catch (_) {
        /* keep session */
      }
    }
  }
  return painted;
}

/**
 * Reformat mounted rows so locale-aware formatters that read a live translator
 * (ref / module translator) refresh without data reload.
 * Does not call replaceData / setData / ajax.
 *
 * @param {object|null} table
 * @returns {{ reformatted: number, reactPainted: number }}
 */
export function reformatTabulatorRowsForLocale(table) {
  if (!table || typeof table.getRows !== 'function') {
    return { reformatted: 0, reactPainted: 0 };
  }

  const rows = collectTabulatorRowsForLocaleReformat(table);
  let reformatted = 0;
  for (const row of rows) {
    try {
      if (typeof row.reformat === 'function') {
        row.reformat();
        reformatted += 1;
      }
    } catch (_) {
      /* keep session */
    }
  }

  const reactPainted = forceReactFormatterLocalePaint(rows);
  return { reformatted, reactPainted };
}

/**
 * Apply full display-layer locale update.
 *
 * @param {object|null} table
 * @param {(key: string, params?: object|null) => string} t
 * @param {{
 *   columnTitleKeys?: Record<string, string>,
 *   headerFilters?: Record<string, { placeholder?: string, options?: Array<{label:string,value:any}>, values?: Record<string,string> }>,
 *   onRefreshCells?: (table: object, t: Function) => void,
 *   reformatRows?: boolean,
 *   placeholder?: string | ((t: Function) => string),
 *   dataLoaderLoading?: string,
 *   dataLoaderError?: string,
 * }} [options]
 */
export function applyTabulatorRuntimeI18n(table, t, options = {}) {
  if (!table) return { applied: false };
  const {
    columnTitleKeys,
    headerFilters,
    onRefreshCells,
    reformatRows = true,
    placeholder,
    dataLoaderLoading,
    dataLoaderError,
  } = options;

  if (columnTitleKeys && typeof t === 'function') {
    applyTabulatorColumnTitles(table, t, columnTitleKeys);
  }

  if (headerFilters) {
    applyTabulatorHeaderFilterDisplay(table, headerFilters);
  }

  if (table.options && typeof table.options === 'object') {
    if (placeholder != null) {
      table.options.placeholder = typeof placeholder === 'function' ? placeholder(t) : placeholder;
    }
    if (dataLoaderLoading != null) table.options.dataLoaderLoading = dataLoaderLoading;
    if (dataLoaderError != null) table.options.dataLoaderError = dataLoaderError;
  }

  if (typeof onRefreshCells === 'function') {
    try {
      onRefreshCells(table, t);
    } catch (_) {
      /* keep session */
    }
  }

  if (reformatRows) {
    reformatTabulatorRowsForLocale(table);
  }

  return { applied: true };
}

/**
 * Decision: native Tabulator `langs` / `setLocale` is NOT wired globally.
 * App chrome (pagination off on most lists, empty state, titles) uses `t()`.
 * Native locale remains unused to avoid competing mechanisms.
 */
export const TABULATOR_NATIVE_LOCALE_POLICY = Object.freeze({
  used: false,
  reason: 'App-level t() owns titles/filters/empty; most lists disable Tabulator pagination. Avoid mixed locale sources.',
});

/**
 * Push fresh column definitions (titles, header filters, formatters) without remount.
 * Complements applyTabulatorRuntimeI18n for lists whose columns useMemo tracks locale.
 *
 * Always ends with a display-only row reformat so reactFormatter cells re-run
 * against live locale refs (setColumns/redraw alone is not enough when
 * visible-row collection was empty or offsetParent skipped reinitialize).
 *
 * @param {object|null} table
 * @param {Array<object>|null} columns
 * @returns {boolean}
 */
export function applyTabulatorColumnsOnLocale(table, columns) {
  if (!table?.setColumns || !Array.isArray(columns)) return false;
  try {
    table.setColumns(columns);
  } catch (_) {
    return false;
  }
  try {
    table.redraw?.(true);
  } catch (_) {
    /* ignore redraw errors */
  }
  // Critical R87: force formatter re-execution after column swap.
  reformatTabulatorRowsForLocale(table);
  return true;
}

/**
 * Canonical display-only locale refresh for progressive lists.
 *
 * R87: do NOT call setColumns by default — progressive ajax lists throw
 * "Data Load Response Blocked" and can leave reactFormatter cells stale.
 * Titles/header-filter labels update in place; mounted cells reformat via
 * row.reformat + reactFormatter onRendered re-paint against live locale refs.
 *
 * Pass `{ setColumns: true, columns }` only for non-progressive tables that
 * truly need full column replacement.
 *
 * @param {object|null} table
 * @param {(key: string, params?: object|null) => string} t
 * @param {Array<object>|null|undefined} columns
 * @param {Parameters<typeof applyTabulatorRuntimeI18n>[2] & { setColumns?: boolean }} [options]
 * @returns {{ applied: boolean }}
 */
export function applyTabulatorLocaleDisplayRefresh(table, t, columns, options = {}) {
  if (!table) return { applied: false };
  const { setColumns: doSetColumns = false, ...i18nOptions } = options;
  applyTabulatorRuntimeI18n(table, t, {
    ...i18nOptions,
    reformatRows: true,
  });
  if (doSetColumns && Array.isArray(columns)) {
    applyTabulatorColumnsOnLocale(table, columns);
  }
  return { applied: true };
}
